import { Controller } from '@hotwired/stimulus'
import {
  APPROVAL_CHANGE_HEADING,
  APPROVAL_CHOOSE_AN_APPROVER,
  APPROVAL_NO_LONGER_REQUIRED,
  APPROVAL_WILL_BE_REQUIRED,
  trans,
} from '../translator'

export default class ApprovalPreviewController extends Controller<HTMLElement> {
  static targets = ['enabled', 'approver', 'consequence', 'save']
  static values = { enabled: Boolean, approver: String }

  declare readonly enabledTarget: HTMLInputElement
  declare readonly approverTarget: HTMLSelectElement
  declare readonly hasApproverTarget: boolean
  declare readonly consequenceTarget: HTMLElement
  declare readonly hasConsequenceTarget: boolean
  declare readonly saveTarget: HTMLButtonElement
  declare readonly hasSaveTarget: boolean
  declare readonly enabledValue: boolean
  declare readonly approverValue: string

  private readonly onChange = (): void => this.refresh()

  connect (): void {
    this.element.addEventListener('change', this.onChange)
    this.refresh()
  }

  disconnect (): void {
    this.element.removeEventListener('change', this.onChange)
  }

  refresh (): void {
    const enabled = this.enabledTarget.checked
    const approver = this.hasApproverTarget ? this.approverTarget.value : ''

    if (this.hasApproverTarget) {
      this.approverTarget.disabled = !enabled
    }

    const changed = enabled !== this.enabledValue || (enabled && approver !== this.approverValue)

    this.render(enabled, approver, changed)

    if (this.hasSaveTarget) {
      this.saveTarget.disabled = !changed || (enabled && approver === '')
    }
  }

  private render (enabled: boolean, approver: string, changed: boolean): void {
    if (!this.hasConsequenceTarget) {
      return
    }

    this.consequenceTarget.textContent = ''

    if (!changed) {
      return
    }

    const heading = document.createElement('p')
    heading.textContent = trans(APPROVAL_CHANGE_HEADING, {}, 'form-types')

    const point = document.createElement('li')

    if (!enabled) {
      point.textContent = trans(APPROVAL_NO_LONGER_REQUIRED, {}, 'form-types')
    } else if (approver === '') {
      point.textContent = trans(APPROVAL_CHOOSE_AN_APPROVER, {}, 'form-types')
    } else {
      const [before, after] = trans(APPROVAL_WILL_BE_REQUIRED, {}, 'form-types').split('%bold%')
      const emphasis = document.createElement('strong')
      emphasis.textContent = this.approverName(approver)

      point.appendChild(document.createTextNode(before ?? ''))
      point.appendChild(emphasis)
      point.appendChild(document.createTextNode(after ?? ''))
    }

    const points = document.createElement('ul')
    points.appendChild(point)

    this.consequenceTarget.appendChild(heading)
    this.consequenceTarget.appendChild(points)
  }

  private approverName (value: string): string {
    if (!this.hasApproverTarget) {
      return value
    }

    const option = this.approverTarget.querySelector(`option[value="${value}"]`)

    return option?.textContent?.trim() ?? value
  }
}
